import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/format";
import { label } from "@/lib/labels";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BrandMark } from "@/components/common/BrandLogo";
import { PhoneFrame } from "@/components/common/PhoneFrame";
import { Home, Truck, AlertTriangle, LogOut, Monitor, ClipboardList, Smartphone, Maximize2, Camera, Navigation, Mic } from "lucide-react";
import "@/components/field/field.css";

const TABS = [
  { to: "/felt", label: "Hjem", icon: Home, end: true, testId: "field-nav-home" },
  { to: "/felt/oppgaver", label: "Oppgaver", icon: ClipboardList, testId: "field-nav-tasks" },
  { to: "/felt/transport", label: "Transport", icon: Truck, testId: "field-nav-transport" },
  { to: "/felt/avvik/ny", label: "Nytt avvik", icon: AlertTriangle, accent: true, testId: "field-nav-issue" },
];

const MOCKUP_KEY = "field.mockup"; // "off" → full bredde også på desktop

/**
 * Feltvisningens skall: topplinje, innhold og bunnmeny. På telefon fyller det skjermen (bunnmeny festet nederst).
 * `embedded` (inne i telefon-rammen på desktop): hele skallet fyller rammen, innholdet ruller inni, bunnmenyen står
 * nederst i rammen – nøyaktig slik det ser ut på en ekte telefon.
 */
function FieldShell({ embedded, onFullWidth }) {
  const { user, logout, isField } = useAuth();
  const navigate = useNavigate();
  return (
    <div className={cn("flex flex-col bg-background", embedded ? "h-full" : "min-h-screen")} data-testid="field-layout" data-embedded={embedded ? "true" : "false"}>
      <header className="sticky top-0 z-30 h-14 shrink-0 border-b border-border/70 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto max-w-2xl h-full px-4 flex items-center justify-between">
          <Link to="/felt" className="flex items-center gap-2.5 min-h-[44px]" data-testid="field-brand-link">
            <BrandMark className="h-7 w-7" color="hsl(var(--foreground))" />
            <div className="leading-tight"><div className="font-display font-semibold text-[14px] tracking-tight">Feltvisning</div><div className="text-[10.5px] text-muted-foreground">Stord Innkvartering</div></div>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted" data-testid="field-user-menu-button" aria-label="Brukermeny">
                <Avatar className="h-8 w-8"><AvatarFallback className="text-[11px] bg-primary/10 text-primary font-semibold">{initials(user?.name)}</AvatarFallback></Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel><div className="text-sm">{user?.name}</div><div className="text-xs text-muted-foreground font-normal">{label(user?.role)}</div></DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!isField && <DropdownMenuItem onClick={() => navigate("/")} data-testid="field-to-desktop-button"><Monitor className="h-4 w-4 mr-2" /> Til kontorvisning</DropdownMenuItem>}
              {embedded && <DropdownMenuItem onClick={onFullWidth} data-testid="field-full-width-button"><Maximize2 className="h-4 w-4 mr-2" /> Vis i full bredde</DropdownMenuItem>}
              <DropdownMenuItem onClick={logout} data-testid="field-logout-button"><LogOut className="h-4 w-4 mr-2" /> Logg ut</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className={cn("flex-1 mx-auto w-full max-w-2xl px-4 py-4", embedded ? "min-h-0 overflow-y-auto overscroll-contain pb-6" : "pb-24")} data-testid="field-main">
        <Outlet />
      </main>
      <nav className={cn("z-30 border-t border-border/70 bg-card/92 backdrop-blur-xl", embedded ? "shrink-0" : "fixed bottom-0 inset-x-0 pb-[env(safe-area-inset-bottom)]")} data-testid="field-bottom-nav" aria-label="Feltnavigasjon">
        <div className="mx-auto max-w-2xl grid grid-cols-4 px-2">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testId} className={({ isActive }) => cn("field-tab", isActive && "is-active")} data-accent={t.accent ? "true" : "false"}>
              <span className="field-tab-ico"><t.icon className="h-5 w-5" /></span>{t.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

const FEATURES = [
  { icon: ClipboardList, title: "Dagens oppgaver og sjekklister", text: "Start, kryss av og fullfør – alt logges på prosjektet." },
  { icon: Mic, title: "Snakk inn avvik og rapporter", text: "Trykk på mikrofonen og fortell. KI strukturerer – prosjektleder varsles med en gang." },
  { icon: Camera, title: "Bilder rett fra byggeplassen", text: "Ta bilde, legg ved – lagret på oppgaven og avviket." },
  { icon: Navigation, title: "Transporter og veibeskrivelse", text: "Hva kommer når, kran eller ikke – og veien dit med ett trykk." },
];

/** Desktop: den ekte mobilappen i en telefon-ramme på en rolig navy-scene, med QR for å åpne på egen telefon. */
function FieldStage({ onFullWidth }) {
  const { isField } = useAuth();
  const navigate = useNavigate();
  const url = `${window.location.origin}/felt`;
  return (
    <div className="field-stage" data-testid="field-stage">
      <div className="module-grid-bg pointer-events-none absolute inset-0 opacity-70" aria-hidden />
      <div className="field-stage-glow" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-navy via-navy/60 to-transparent" aria-hidden />

      <header className="relative z-10 flex items-center justify-between gap-4 px-6 py-4 xl:px-10" data-testid="field-stage-header">
        <div className="flex items-center gap-2.5">
          <BrandMark className="h-7 w-7" color="#fff" />
          <div className="leading-tight">
            <div className="text-[14px] font-semibold tracking-tight">Stord Innkvartering</div>
            <div className="text-[11.5px] text-white/60">Feltvisning · mobil</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={onFullWidth} data-testid="field-stage-full-width"><Maximize2 className="mr-1.5 h-4 w-4" /> Full bredde</Button>
          {!isField && <Button variant="secondary" size="sm" onClick={() => navigate("/")} data-testid="field-stage-to-desktop"><Monitor className="mr-1.5 h-4 w-4" /> Til kontorvisning</Button>}
        </div>
      </header>

      <div className="relative z-10 mx-auto grid w-full max-w-[1240px] grid-cols-[minmax(0,1fr)_auto] items-center gap-12 px-6 pb-10 pt-4 xl:gap-20 xl:px-10">
        <div className="field-stage-copy">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Mobilappen for montører</div>
          <h1 className="font-display mt-3 text-[34px] font-semibold leading-[1.08] tracking-tight xl:text-[40px]">Slik ser montøren det på mobilen</h1>
          <p className="mt-3 text-[14.5px] leading-6 text-white/75">Dette er den ekte appen, live med dine data. Alt du gjør her skjer også i systemet.</p>
          <ul className="mt-7 space-y-4">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3.5" data-testid="field-stage-feature">
                <span className="field-feature-icon"><f.icon className="h-[17px] w-[17px]" /></span>
                <div><div className="text-[14px] font-semibold leading-5">{f.title}</div><div className="text-[13px] leading-5 text-white/65">{f.text}</div></div>
              </li>
            ))}
          </ul>
          <div className="field-qr-card mt-8" data-testid="field-stage-qr">
            <div className="field-qr-code"><QRCodeSVG value={url} size={92} level="M" bgColor="transparent" fgColor="#0B1220" /></div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[15px] font-semibold"><Smartphone className="h-4 w-4" /> Åpne på mobilen</div>
              <div className="mt-0.5 text-[13px] text-muted-foreground">Skann og logg inn med samme bruker.</div>
              <div className="mt-2 text-[12px] font-semibold text-primary [overflow-wrap:anywhere]" data-testid="field-stage-url">{url.replace(/^https?:\/\//, "")}</div>
            </div>
          </div>
        </div>
        <div className="field-stage-phone">
          <PhoneFrame testId="field-phone">
            <FieldShell embedded onFullWidth={onFullWidth} />
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

export default function FieldLayout() {
  const desktop = useMediaQuery("(min-width: 1024px) and (pointer: fine)");
  const [mockup, setMockup] = useState(() => localStorage.getItem(MOCKUP_KEY) !== "off");
  useEffect(() => { localStorage.setItem(MOCKUP_KEY, mockup ? "on" : "off"); }, [mockup]);
  const fullWidth = useCallback(() => setMockup(false), []);
  if (desktop && mockup) return <FieldStage onFullWidth={fullWidth} />;
  return (
    <>
      <FieldShell embedded={false} />
      {desktop && (
        <Button variant="outline" size="sm" className="fixed bottom-5 right-5 z-40 shadow-pop" onClick={() => setMockup(true)} data-testid="field-show-mockup-button"><Smartphone className="mr-1.5 h-4 w-4" /> Vis i mobilramme</Button>
      )}
    </>
  );
}
